import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Empty, Space, Tag, Typography } from "antd";
import {
  agentPlatformApi,
  AgentPlatformApiError,
  Issue,
  IssueAction,
} from "./api";
import { isSafeBriefSourceUrl } from "./Briefs";
import { EvaluationFeedback } from "./Evaluation";
import { formatAgentDateTime } from "./dateTime";

const { Title, Text } = Typography;
const key = () =>
  `agent-platform-issue-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const statusColor = (status: string) =>
  status === "resolved"
    ? "green"
    : status === "dismissed"
    ? "default"
    : status === "needs_user"
    ? "gold"
    : "blue";
const isCancelled = (error: unknown) =>
  (error as { code?: string })?.code === "ERR_CANCELED";
const errorCopy = (error: unknown) =>
  error instanceof AgentPlatformApiError && error.status === 409
    ? "This issue changed. Refresh to review its latest status."
    : error instanceof AgentPlatformApiError && error.status === 404
    ? "Issues are not available for this workspace."
    : error instanceof Error
    ? error.message
    : "Unable to load issues.";
const nextDay = () => new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
const futureSnoozeLabel = (value?: string): string | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) || date <= new Date()
    ? undefined
    : `Snoozed until ${formatAgentDateTime(value) || "Time unavailable"}`;
};
const groupFor = (issue: Issue): string => {
  if (issue.status === "dismissed") return "Dismissed";
  if (issue.status === "resolved") return "Resolved";
  if (futureSnoozeLabel(issue.snoozeUntil)) return "Snoozed";
  if (issue.attentionState === "waiting_on_you" || issue.status === "needs_user") return "Waiting on you";
  if (issue.attentionState === "watching" || issue.status === "waiting") return "Watching";
  return "Needs attention";
};
const groups = ["Needs attention", "Waiting on you", "Watching", "Resolved", "Snoozed", "Dismissed"];
const dateLabel = (value: string) => formatAgentDateTime(value) || "Time unavailable";

export default function Issues({
  workspaceId,
  refreshToken = 0,
  focusIssueId,
}: {
  workspaceId: string;
  refreshToken?: number;
  focusIssueId?: string;
}): React.ReactElement {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState<string>();
  const mounted = useRef(true);
  const version = useRef(0);
  const controller = useRef<AbortController>();
  const retryPayloads = useRef(
    new Map<
      string,
      { expectedRevision: number; idempotencyKey: string; snoozeUntil?: string }
    >()
  );
  const load = useCallback(async () => {
    const current = ++version.current;
    controller.current?.abort();
    const signal = new AbortController();
    controller.current = signal;
    setLoading(true);
    setError(undefined);
    try {
      const result = await agentPlatformApi.issues(workspaceId, signal.signal);
      if (!mounted.current || current !== version.current) return;
      setEnabled(result.enabled);
      setIssues(result.items);
    } catch (loadError) {
      if (
        !mounted.current ||
        current !== version.current ||
        isCancelled(loadError)
      )
        return;
      if (
        loadError instanceof AgentPlatformApiError &&
        loadError.status === 404
      ) {
        setEnabled(false);
        setIssues([]);
      } else {
        setIssues([]);
        setError(errorCopy(loadError));
      }
    } finally {
      if (mounted.current && current === version.current) setLoading(false);
    }
  }, [workspaceId, refreshToken]);
  useEffect(() => {
    mounted.current = true;
    setPending(undefined);
    void load();
    return () => {
      mounted.current = false;
      version.current += 1;
      controller.current?.abort();
    };
  }, [load]);
  const feedback = async (issue: Issue, action: IssueAction) => {
    if (pending) return;
    const current = version.current;
    const scope = `${workspaceId}:${issue.id}:${action}`;
    const payload = retryPayloads.current.get(scope) || {
      expectedRevision: issue.revision,
      idempotencyKey: key(),
      ...(action === "snooze" ? { snoozeUntil: nextDay() } : {}),
    };
    retryPayloads.current.set(scope, payload);
    setPending(scope);
    setError(undefined);
    try {
      const result = await agentPlatformApi.issueFeedback(
        workspaceId,
        { ...issue, revision: payload.expectedRevision },
        action,
        payload.idempotencyKey,
        payload.snoozeUntil
      );
      if (!mounted.current || current !== version.current) return;
      retryPayloads.current.delete(scope);
      setIssues((items) =>
        items.map((item) => (item.id === result.issue.id ? result.issue : item))
      );
    } catch (feedbackError) {
      if (
        feedbackError instanceof AgentPlatformApiError &&
        feedbackError.status === 409
      )
        retryPayloads.current.delete(scope);
      if (mounted.current && current === version.current)
        setError(errorCopy(feedbackError));
    } finally {
      if (mounted.current && current === version.current) setPending(undefined);
    }
  };
  const source = (issue: Issue) =>
    issue.sources?.map((item) =>
      isSafeBriefSourceUrl(item.url, item.kind) ? (
        <a key={item.id} href={item.url} target="_blank" rel="noreferrer">
          {item.title}
        </a>
      ) : (
        <span key={item.id}>{item.title}</span>
      )
    );
  if (!enabled && !loading && !error) return <></>;
  return (
    <section className="agent-platform-issues" aria-labelledby="issues-title">
      <Space className="agent-platform-briefs-heading">
        <div>
          <Title id="issues-title" level={3}>
            Workspace attention
          </Title>
          <Text type="secondary">
            Current issues and changes. Mark changes as seen without closing the issue.
          </Text>
        </div>
        <Button
          onClick={() => void load()}
          disabled={loading || Boolean(pending)}
        >
          Refresh issues
        </Button>
      </Space>
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          action={
            <Button size="small" onClick={() => void load()}>
              Refresh
            </Button>
          }
        />
      )}
      {loading ? (
        <Text type="secondary">Loading saved issues…</Text>
      ) : issues.length ? (
        <Space direction="vertical" className="agent-platform-full">
          {groups.map((group) => {
            const grouped = issues.filter((issue) => groupFor(issue) === group);
            if (!grouped.length) return null;
            const cards = <div className="agent-platform-grid">{grouped.map((issue) => {
            const scope = `${workspaceId}:${issue.id}`;
            const busy = Boolean(pending?.startsWith(scope));
            return (
              <Card
                key={issue.id}
                id={focusIssueId === issue.id ? `agent-platform-issue-${issue.id}` : undefined}
                tabIndex={focusIssueId === issue.id ? -1 : undefined}
                size="small"
                title={
                  <Space>
                    <Tag color={statusColor(issue.status)}>{issue.status}</Tag>
                    <Text>{issue.title}</Text>
                  </Space>
                }
              >
                <Space
                  direction="vertical"
                  className="agent-platform-full"
                  size="small"
                >
                  <Text>{issue.detail}</Text>
                  {(issue.attentionVersion || 0) > (issue.acknowledgedVersion || 0) && !["resolved", "dismissed"].includes(issue.status) && <Tag color="blue">New change</Tag>}
                  {source(issue)?.length ? (
                    <Text type="secondary">Sources: {source(issue)}</Text>
                  ) : null}
                <Text type="secondary">Last checked: {issue.lastCheckedAt ? dateLabel(issue.lastCheckedAt) : "Not recorded"}</Text>
                {issue.nextCheckAt && !["resolved", "dismissed"].includes(issue.status) && <Text type="secondary">Next check: {dateLabel(issue.nextCheckAt)}</Text>}
                {issue.stale && <Tag color="gold">Awaiting a fresh check</Tag>}
                  {futureSnoozeLabel(issue.snoozeUntil) && (
                    <Text type="secondary">
                      {futureSnoozeLabel(issue.snoozeUntil)}
                    </Text>
                  )}
                  <Space wrap>
                    {issue.status !== "resolved" && (
                      <Button
                        onClick={() => void feedback(issue, "resolve")}
                        disabled={busy || Boolean(pending)}
                      >
                        Resolve
                      </Button>
                    )}
                  {issue.status !== "dismissed" && (
                      <Button
                        onClick={() => void feedback(issue, "dismiss")}
                        disabled={busy || Boolean(pending)}
                      >
                        Dismiss
                      </Button>
                  )}
                  {(issue.attentionVersion || 0) > (issue.acknowledgedVersion || 0) && !["resolved", "dismissed"].includes(issue.status) && <Button onClick={() => void feedback(issue, "acknowledge")} disabled={busy || Boolean(pending)}>Acknowledge changes</Button>}
                    {["resolved", "dismissed"].includes(issue.status) && (
                      <Button
                        onClick={() => void feedback(issue, "reopen")}
                        disabled={busy || Boolean(pending)}
                      >
                        Reopen
                      </Button>
                    )}
                    {!["resolved", "dismissed"].includes(issue.status) && (
                      <Button
                        onClick={() => void feedback(issue, "snooze")}
                        disabled={busy || Boolean(pending)}
                      >
                        Snooze 1 day
                      </Button>
                    )}
                  </Space>
                  <EvaluationFeedback key={`${workspaceId}:${issue.id}`} workspaceId={workspaceId} targetType="issue" targetId={issue.id} />
                </Space>
              </Card>
            );
          })}</div>;
          return ["Resolved", "Snoozed", "Dismissed"].includes(group)
            ? <details key={group} className="agent-platform-brief-details"><summary>{group} ({grouped.length})</summary>{cards}</details>
            : <section key={group} aria-label={group}><Title level={4}>{group} ({grouped.length})</Title>{cards}</section>;
          })}
        </Space>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No saved attention items."
        />
      )}
    </section>
  );
}
